import ChartLegend from '@app/components/charts/legends/chart-legend';
import { generateEvenTicks } from '@app/components/reports/utils';
import SimpleLineChart from '@shared/components/chart/simple-line-chart';
import { truncateLabel } from '@shared/components/chart/utils';
import { ChartCard } from '@shared/components/dashboard/chart-card';
import { DashboardGrid } from '@shared/components/dashboard/dashboard-grid';
import { DashboardSection } from '@shared/components/dashboard/dashboard-section';
import { MetricCard } from '@shared/components/dashboard/metric-card';
import { MetricStack } from '@shared/components/dashboard/metric-stack';
import { Badge } from '@shared/components/ui/badge';
import { ChartConfig } from '@shared/components/ui/chart';
import { getCurrencySymbol } from '@shared/lib/format';
import { cn } from '@shared/lib/utils';
import { PurchasedGoodsSpend, TimePeriod } from '@shared/api/types';
import { useMemo } from 'react';
import { ReferenceLine } from 'recharts';
import { PriceChangeBadge } from './price-change-badge';

const MONTH_YEAR = new Intl.DateTimeFormat('en-GB', {
  month: 'short',
  year: '2-digit',
});
const MONTH_ONLY = new Intl.DateTimeFormat('en-GB', { month: 'short' });

interface PriceChangesSectionProps {
  spend: PurchasedGoodsSpend;
  currency: string | null | undefined;
  unitSymbol: string;
  monthlyPeriods: TimePeriod[];
}

export function PriceChangesSection({
  spend,
  currency,
  unitSymbol,
  monthlyPeriods,
}: PriceChangesSectionProps) {
  const symbol = getCurrencySymbol(currency);
  const perUnit = `/ ${unitSymbol}`;

  const averagePrice =
    spend.averagePricePerUnit != null
      ? Number(spend.averagePricePerUnit)
      : null;
  const firstPrice =
    spend.firstTimePeriodPricePerUnit != null
      ? Number(spend.firstTimePeriodPricePerUnit)
      : null;
  const lastPrice =
    spend.lastTimePeriodPricePerUnit != null
      ? Number(spend.lastTimePeriodPricePerUnit)
      : null;
  const priceChange = spend.priceChangePerUnit;
  const impact =
    spend.impactOfPriceChange != null
      ? Number(spend.impactOfPriceChange)
      : null;

  const priceChangePct =
    firstPrice != null && priceChange != null && firstPrice !== 0
      ? (priceChange / firstPrice) * 100
      : null;

  const chartData = useMemo(() => {
    const byUuid = new Map(
      spend.spendOverTime.map((month) => [
        month.timePeriodUuid,
        month.spendPerQuantity != null ? Number(month.spendPerQuantity) : null,
      ])
    );
    const actuals: (number | null)[] = monthlyPeriods.map(
      (period) => byUuid.get(period.uuid) ?? null
    );
    const firstIdx = actuals.findIndex((v) => v != null);
    const firstActual = firstIdx >= 0 ? actuals[firstIdx] : null;
    let lastKnown: number | null = firstActual;
    let prevActual: number | null = null;
    return monthlyPeriods.map((period, i) => {
      const actual = actuals[i];
      let price: number | null;
      if (actual != null) {
        price = actual;
        lastKnown = actual;
      } else if (firstActual == null) {
        price = null;
      } else if (i < firstIdx) {
        // Extend the first known price back to the start of the chart.
        price = firstActual;
      } else {
        // Hold the last known price forward through gaps and past the end.
        price = lastKnown;
      }
      let pct: number | null = null;
      if (
        actual != null &&
        prevActual != null &&
        prevActual !== 0 &&
        actual !== prevActual
      ) {
        pct = ((actual - prevActual) / prevActual) * 100;
      }
      if (actual != null) prevActual = actual;
      return {
        period: period.name,
        price,
        changePct: pct,
      };
    });
  }, [spend.spendOverTime, monthlyPeriods]);

  const priceTicks = useMemo<number[]>(() => {
    const values = chartData
      .map((d) => d.price)
      .filter((v): v is number => v != null);
    if (averagePrice != null) values.push(averagePrice);
    if (values.length === 0) return [0, 1];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || max * 0.1 || 0.1;
    const pad = range * 0.25;
    return generateEvenTicks(max + pad, 5, Math.max(0, min - pad));
  }, [chartData, averagePrice]);

  const priceDomain: [number, number] = [
    priceTicks[0],
    priceTicks[priceTicks.length - 1],
  ];

  const firstSpendPeriod = spend.spendOverTime[0];
  const lastSpendPeriod = spend.spendOverTime[spend.spendOverTime.length - 1];

  const firstMonthLabel = firstSpendPeriod
    ? MONTH_YEAR.format(new Date(firstSpendPeriod.timePeriodStartDate))
    : '';
  const lastMonthLabel = lastSpendPeriod
    ? MONTH_YEAR.format(new Date(lastSpendPeriod.timePeriodStartDate))
    : '';
  const firstMonthOnly = (() => {
    if (!firstSpendPeriod) return '';
    const start = MONTH_ONLY.format(
      new Date(firstSpendPeriod.timePeriodStartDate)
    );
    const end = MONTH_ONLY.format(new Date(firstSpendPeriod.timePeriodEndDate));
    return start === end ? start : `${start} - ${end}`;
  })();

  const chartConfig = {
    price: {
      label: 'Month average',
      color: 'var(--chart-cost)',
    },
  } satisfies ChartConfig;

  const formatPrice = (value: number) =>
    `${symbol}${value.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatPriceNumber = (value: number) =>
    value.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // Render the currency symbol in the muted unit colour, spaced off the figure,
  // so it reads as a unit rather than blending into the number. An optional
  // sign (+/-) stays in the value colour, ahead of the symbol.
  const withCurrency = (formatted: string, sign = '') =>
    symbol ? (
      <>
        {sign}
        <span className="mr-1 text-primary-500">{symbol}</span>
        {formatted}
      </>
    ) : (
      `${sign}${formatted}`
    );

  const changeIsPositive = priceChange != null && priceChange > 0;
  const changeIsNegative = priceChange != null && priceChange < 0;

  return (
    <DashboardSection
      title="Price changes"
      description={`Understand how the price per ${unitSymbol} has changed over time, based on the spend divided by amount purchased each month`}
    >
      <DashboardGrid>
        <MetricStack>
          <MetricCard
            title={`Average price per ${unitSymbol}`}
            value={
              averagePrice != null
                ? withCurrency(formatPriceNumber(averagePrice))
                : '—'
            }
            unit={averagePrice != null ? perUnit : undefined}
            description="Weighted average over the period"
          />
          <MetricCard
            title="Price change"
            value={
              priceChange != null
                ? withCurrency(
                    formatPriceNumber(Math.abs(priceChange)),
                    changeIsPositive ? '+' : changeIsNegative ? '-' : ''
                  )
                : '—'
            }
            unit={priceChange != null ? perUnit : undefined}
            badge={
              priceChangePct != null && priceChange !== 0 ? (
                <Badge
                  variant="default"
                  size="sm"
                  className={cn(
                    'border-transparent',
                    changeIsPositive && 'bg-error-50 text-error-700',
                    changeIsNegative && 'bg-secondary-50 text-secondary-700'
                  )}
                >
                  {changeIsPositive ? '+' : ''}
                  {priceChangePct.toFixed(1)}%
                </Badge>
              ) : undefined
            }
            description={
              firstPrice != null && lastPrice != null
                ? `From ${formatPrice(firstPrice)} in ${firstMonthLabel} to ${formatPrice(lastPrice)} in ${lastMonthLabel}`
                : undefined
            }
          />
          <MetricCard
            title="Impact of price change"
            value={
              impact != null
                ? withCurrency(
                    Math.abs(Math.round(impact)).toLocaleString('en-GB'),
                    impact > 0 ? '+' : impact < 0 ? '-' : ''
                  )
                : '—'
            }
            description={`Additional spend in period vs. ${firstMonthOnly} price`}
          />
        </MetricStack>

        <ChartCard
          title={`Price per ${unitSymbol} over time`}
          className="lg:col-span-2"
          legend={
            <>
              <ChartLegend name="Month average" color="var(--chart-cost)" />
              <div className="flex items-center space-x-2">
                <div className="h-px w-4 shrink-0 border-t border-dashed border-gray-400" />
                <span className="text-sm font-light">Period average</span>
              </div>
            </>
          }
        >
          <SimpleLineChart
            chartConfig={chartConfig}
            chartData={chartData}
            className="aspect-auto h-full w-full"
            nameKey="period"
            dataKey="price"
            lineType="stepAfter"
            margin={{ top: 20, right: 16, bottom: 40, left: 20 }}
            XAxisProps={{
              // Cap at ~8 evenly spaced labels so long windows stay legible.
              interval: Math.max(0, Math.ceil(chartData.length / 8) - 1),
              angle: -30,
              textAnchor: 'end',
              tickMargin: 2,
              dx: 10,
              tickFormatter: (value: string) => truncateLabel(value, 15),
            }}
            YAxisProps={{
              tickMargin: 8,
              width: 56,
              domain: priceDomain,
              ticks: priceTicks,
              tickFormatter: (value: number) => formatPrice(value),
            }}
            tooltipValueFormatter={(value) =>
              `${
                typeof value === 'number' ? formatPrice(value) : value
              }${perUnit}`
            }
            referenceLines={
              averagePrice != null ? (
                <ReferenceLine
                  y={averagePrice}
                  stroke="var(--muted-foreground)"
                  strokeDasharray="4 4"
                  label={{
                    value: `avg ${formatPrice(averagePrice)}`,
                    position: 'insideTopRight',
                    dy: -20,
                    fill: 'var(--muted-foreground)',
                    fontSize: 11,
                  }}
                />
              ) : undefined
            }
            lineLabel={({ x, y, index }) => {
              const pct = chartData[index]?.changePct;
              if (pct == null) return null;
              return <PriceChangeBadge x={x} y={y} pct={pct} />;
            }}
          />
        </ChartCard>
      </DashboardGrid>
    </DashboardSection>
  );
}
